import { bindExample } from '@cogo/binder'
import type { CogoKnowledgeRepository } from '@cogo/kg-repository'
import type { BlockDef } from '@cogo/types-v2'
import { ONTOLOGY_CANDIDATE_QUALITY_FLOOR, type ChannelInput } from '../_shared/channel-types'
import { extractBindDefaults } from '../channel-1/bind-defaults-extractor'
import { defaultResolvers } from '../channel-1/default-resolvers'

export interface ResolvedExample {
  id: string
  pageId: string         // 'login', 'dashboard', ...
  pageName: string       // 'Login', 'Dashboard', ...
  boundBlock: BlockDef
  bindWarnings: string[]
  exampleTags: string[]
  semanticType?: string
  patternRef?: string
  score?: number
  intentMatch?: number
  qualityGrade?: number
}

export type PickResult =
  | { kind: 'ok'; examples: ResolvedExample[]; totalBindMs: number }
  | { kind: 'insufficient'; available: number; required: number; rejectedForQuality: number }

// FNV-1a 32-bit hash for intent → seed.
export function hashIntent(input: ChannelInput): number {
  const str =
    input.kind === 'intent'
      ? input.pattern || input.semanticType || 'default'
      : input.exampleId
  let h = 2166136261 >>> 0
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 16777619) >>> 0
  }
  return h
}

// Mulberry32 PRNG — seed → next() returns [0,1) deterministic.
function mulberry32(seed: number) {
  let s = seed >>> 0
  return () => {
    s = (s + 0x6D2B79F5) >>> 0
    let t = s
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export function deterministicShuffle<T>(arr: readonly T[], seed: number): T[] {
  const result = [...arr]
  const rand = mulberry32(seed)
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1))
    ;[result[i], result[j]] = [result[j], result[i]]
  }
  return result
}

export const FALLBACK_PAGE_NAMES = ['Login', 'Dashboard', 'Tasks', 'Settings', 'Profile'] as const

export function resolvePageName(tags: readonly string[] | undefined, index: number): string {
  const firstTag = tags?.[0]
  if (firstTag) {
    return firstTag.charAt(0).toUpperCase() + firstTag.slice(1)
  }
  return FALLBACK_PAGE_NAMES[index] ?? `Page ${index + 1}`
}

function coerceStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
}

function coerceNumber(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined
  return value
}

function qualityIssue(ex: { qualityGrade?: unknown }): string | null {
  const grade = coerceNumber(ex.qualityGrade)
  if (grade === undefined) return `missing rendered design-quality grade; floor=${ONTOLOGY_CANDIDATE_QUALITY_FLOOR}`
  if (grade < ONTOLOGY_CANDIDATE_QUALITY_FLOOR) {
    return `rendered design-quality grade below floor (${grade} < ${ONTOLOGY_CANDIDATE_QUALITY_FLOOR})`
  }
  return null
}

function firstString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) return value
    if (Array.isArray(value)) {
      const first = value.find((entry) => typeof entry === 'string' && entry.trim().length > 0)
      if (typeof first === 'string') return first
    }
  }
  return undefined
}

// kg-repository's findExamples requires a SPARQL anchor (pattern or semanticType)
// to construct a valid query. Broad queries with no anchor return 0 examples.
// Channel 2 mock falls back through these semanticType anchors when the pattern
// query is insufficient — mirrors A3.1 channel-1 dispatch.ts approach.
const SEMANTIC_TYPE_FALLBACKS = [
  'https://cogo.xyz/instances/semantic-type/section',
  'https://cogo.xyz/instances/semantic-type/container',
  'https://cogo.xyz/instances/semantic-type/card',
  'https://cogo.xyz/instances/semantic-type/form',
] as const

export async function pickCorpusForChannelTwo(
  input: ChannelInput,
  kg: CogoKnowledgeRepository,
  count = 5,
): Promise<PickResult> {
  const fetchK = count * 4
  const seenIds = new Set<string>()
  const accumulated: any[] = []
  let rejectedForQuality = 0

  const maybeAccumulate = (ex: any) => {
    if (seenIds.has(ex.id)) return
    seenIds.add(ex.id)
    if (qualityIssue(ex)) {
      rejectedForQuality += 1
      return
    }
    accumulated.push(ex)
  }

  // 1. First try the input's pattern/semanticType (sanitize space → kebab-case).
  if (input.kind === 'intent') {
    const findArgs: any = { k: fetchK }
    if (input.pattern) findArgs.pattern = input.pattern.trim().replace(/\s+/g, '-').toLowerCase()
    if (input.semanticType) findArgs.semanticType = input.semanticType
    if (input.dsId) findArgs.dsId = input.dsId
    if (Object.keys(findArgs).length > 1) {
      try {
        const r = await kg.findExamples(findArgs)
        for (const ex of r.data) {
          maybeAccumulate(ex)
        }
      } catch {
        // pattern parsing failures (e.g., invalid IRI fragments) — fall through to anchors
      }
    }
  }

  // 2. Fall back to semanticType anchors until we have enough.
  if (accumulated.length < count) {
    for (const anchor of SEMANTIC_TYPE_FALLBACKS) {
      if (accumulated.length >= count) break
      try {
        const r = await kg.findExamples({ semanticType: anchor, k: fetchK } as never)
        for (const ex of r.data) {
          maybeAccumulate(ex)
        }
      } catch {
        // skip anchor on failure
      }
    }
  }

  if (accumulated.length < count) {
    return { kind: 'insufficient', available: accumulated.length, required: count, rejectedForQuality }
  }

  // 3. Deterministic shuffle.
  const seed = hashIntent(input)
  const shuffled = deterministicShuffle(accumulated, seed)
  const picked = shuffled.slice(0, count)

  // 3. Bind each via A3.1 defaultResolvers.
  let totalBindMs = 0
  const examples: ResolvedExample[] = []
  for (let i = 0; i < picked.length; i++) {
    const ex = picked[i] as any
    const template = ex.template ?? ex.blockJson
    if (!template) continue
    const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now())
    const blockDefaults = extractBindDefaults(template, ex.blockJson)
    const resolvers = defaultResolvers(ex.bindingSchema, undefined, blockDefaults)
    const bindResult = bindExample(template, resolvers)
    const bindMs = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0
    totalBindMs += bindMs

    const pageName = resolvePageName(ex.exampleTags, i)
    const exampleTags = coerceStringArray(ex.exampleTags)
    examples.push({
      id: ex.id,
      pageId: pageName.toLowerCase().replace(/\s+/g, '-'),
      pageName,
      boundBlock: bindResult.output as never,
      bindWarnings: bindResult.warnings,
      exampleTags,
      semanticType: firstString(ex.semanticType, ex.semanticTypes, ex.type, exampleTags),
      patternRef: firstString(ex.patternRef, ex.patternId, ex.pattern, ex.id) ?? ex.id,
      score: coerceNumber(ex.score),
      intentMatch: coerceNumber(ex.intentMatch),
      qualityGrade: coerceNumber(ex.qualityGrade),
    })
  }

  if (examples.length < count) {
    return { kind: 'insufficient', available: examples.length, required: count, rejectedForQuality }
  }

  return { kind: 'ok', examples, totalBindMs }
}
